import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Modal from "../ui/Modal";
import Button from "../ui/Button";
import Input from "../ui/Input";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as unitActions from "src/redux/slices/unitSlice";
import { Unit } from "src/types/unit.types";

interface AddUnitModalProps {
  unit: Unit | null;
  onClose: () => void;
  onSuccess: () => void;
}

const AddUnitModal: React.FC<AddUnitModalProps> = ({
  unit,
  onClose,
  onSuccess,
}) => {
  const isEdit = !!unit?._id;
  const [name, setName] = useState(unit?.name || "");
  const [shortName, setShortName] = useState(unit?.shortName || "");
  const [touched, setTouched] = useState(false);
  const dispatch = useAppDispatch();
  const { createUnit, editUnit } = useAppSelector((state) => state.unit);
  const loading = isEdit ? editUnit.loading : createUnit.loading;

  useEffect(() => {
    if (isEdit && editUnit.data) {
      toast.success("Unit updated successfully");
      dispatch(unitActions.resetEditUnit());
      onSuccess();
    } else if (isEdit && editUnit.error) {
      toast.error(`Error: ${editUnit.error}`);
      dispatch(unitActions.resetEditUnit());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editUnit]);

  useEffect(() => {
    if (!isEdit && createUnit.data) {
      toast.success("Unit created successfully");
      dispatch(unitActions.resetCreateUnit());
      onSuccess();
    } else if (!isEdit && createUnit.error) {
      toast.error(`Error: ${createUnit.error}`);
      dispatch(unitActions.resetCreateUnit());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createUnit]);

  const handleSubmit = () => {
    setTouched(true);
    if (!name.trim()) return;
    const payload = { name: name.trim(), shortName: shortName.trim() || undefined };
    if (isEdit) {
      dispatch(unitActions.editUnitRequest({ _id: unit!._id, ...payload }));
    } else {
      dispatch(unitActions.createUnitRequest(payload));
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={isEdit ? "Edit unit" : "Add unit"}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} isLoading={loading} disabled={loading}>
            {isEdit ? "Save changes" : "Add unit"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Input
          label="Unit name"
          placeholder="e.g. Kilogram"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          error={touched && !name.trim()}
          helperText={touched && !name.trim() ? "Unit name is required" : undefined}
        />
        <Input
          label="Short name"
          optional
          placeholder="e.g. kg"
          value={shortName}
          onChange={(e) => setShortName(e.target.value)}
        />
      </div>
    </Modal>
  );
};

export default AddUnitModal;
